import {
  AppBar,
  Toolbar,
  Box,
  Button,
  ButtonGroup,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Snackbar,
  ListItemIcon,
  ListItemText,
  Fade,
  Typography,
} from "@mui/material";
import {
  LayoutDashboard,
  Rows3,
  CircleChevronDown,
  Share2,
  FileSpreadsheet,
  Upload,
  UserPlus,
  Mail,
  Settings2,
  ChevronDown,
  CalendarDays,
} from "lucide-react";

import GoogleSheetsModal from "./GoogleSheetsModal";
import UploadSheetModal from "./UploadSheetModal";
import AutoAssignSettings from "./AutoAssignSettings";
import LeadNotificationSettings from "./LeadNotificationSettings";
import ExportSelectionModal from "./ExportSelectionModal";
import { VIEW_TYPES } from "../utils/constants";
import { exportDealsToExcel } from "../utils/exportUtils";
import React, { useState } from "react";

export const Navbar = ({
  currentView,
  onViewChange,
  onAddDeal,
  onRefreshDeals,
  totalDeals = 0,
  fetchAllDeals,
  pipeline = "meta",
  onPipelineChange,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [actionAnchorEl, setActionAnchorEl] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [googleSheetsModalOpen, setGoogleSheetsModalOpen] = useState(false);
  const [uploadSheetModalOpen, setUploadSheetModalOpen] = useState(false);
  const [autoAssignModalOpen, setAutoAssignModalOpen] = useState(false);
  const [notificationModalOpen, setNotificationModalOpen] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const userId = localStorage.getItem("userId");
  const userRole = localStorage.getItem("userRole");

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleActionMenuOpen = (event) => {
    setActionAnchorEl(event.currentTarget);
  };

  const handleActionMenuClose = () => {
    setActionAnchorEl(null);
  };

  const handleShareLink = () => {
    const link = `${window.location.origin}/lead-form/${userId}`;
    navigator.clipboard.writeText(link);
    setSnackbarOpen(true);
    handleActionMenuClose();
  };

  // The board/list only hold the loaded page(s), so export fetches every
  // lead matching the current filters first
  const handleExportLeads = async (selectedFields) => {
    setExporting(true);
    try {
      exportDealsToExcel(await fetchAllDeals(), selectedFields);
    } catch (error) {
      console.error("Error exporting leads:", error);
    } finally {
      setExporting(false);
    }
  };

  const getIconButtonSx = (isActive) => ({
    color: isActive ? "#6366f1" : "#6B7280",
    border: "1px solid",
    borderColor: isActive ? "#6366f1" : "#E5E7EB",
    bgcolor: isActive ? "#EEF2FF" : "transparent",
    borderRadius: "22px",
    textTransform: "capitalize",
    px: 1.5,
    transition: "all 0.2s ease",
    "&:hover": {
      bgcolor: isActive ? "#E0E7FF" : "#F9FAFB",
      borderColor: isActive ? "#4f46e5" : "#D1D5DB",
      transform: "translateY(-1px)",
    },
  });

  return (
    <>
      <AppBar position="static" sx={{ bgcolor: "white", boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)", borderBottom: "1px solid #F3F4F6" }}>
        <Toolbar sx={{ justifyContent: "space-between", px: { xs: 1, sm: 4 }, py: 0.4 }}>
          {/* Left Side - Navigation Views */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
            <ButtonGroup variant="text" sx={{ display: { xs: "none", sm: "flex" }, gap: 1.5 }}>
              <Tooltip title="Pipeline Board" arrow>
                <IconButton
                  onClick={() => onViewChange(VIEW_TYPES.BOARD)}
                  sx={getIconButtonSx(currentView === VIEW_TYPES.BOARD)}
                >
                  <LayoutDashboard size={20} />
                </IconButton>
              </Tooltip>

              <Tooltip title="Leads List" arrow>
                <IconButton
                  onClick={() => onViewChange(VIEW_TYPES.LIST)}
                  sx={getIconButtonSx(currentView === VIEW_TYPES.LIST)}
                >
                  <Rows3 size={20} />
                </IconButton>
              </Tooltip>

              <Tooltip title="Follow Ups" arrow>
                <IconButton
                  onClick={() => onViewChange(VIEW_TYPES.APPOINTMENTS)}
                  sx={getIconButtonSx(currentView === VIEW_TYPES.APPOINTMENTS)}
                >
                  <CalendarDays size={20} />
                </IconButton>
              </Tooltip>
            </ButtonGroup>
            {/* 
            <ButtonGroup
              variant="outlined"
              size="small"
              sx={{
                borderRadius: "20px",
                overflow: "hidden",
                "& .MuiButton-root": {
                  border: "1px solid #E5E7EB",
                  px: 2.5,
                  py: 0.6,
                  fontSize: "0.75rem",
                  fontWeight: 700,
                  textTransform: "capitalize",
                },
              }}
            >
              <Button
                onClick={() => onPipelineChange("meta")}
                sx={{
                  bgcolor: pipeline === "meta" ? "#6366f1" : "transparent",
                  color: pipeline === "meta" ? "white" : "#4B5563",
                  borderRight: "1px solid #E5E7EB !important",
                  "&:hover": {
                    bgcolor: pipeline === "meta" ? "#4f46e5" : "#F9FAFB",
                  },
                }}
              >
                Meta Board
              </Button>
              <Button
                onClick={() => onPipelineChange("whatsapp")}
                sx={{
                  bgcolor: pipeline === "whatsapp" ? "#6366f1" : "transparent",
                  color: pipeline === "whatsapp" ? "white" : "#4B5563",
                  "&:hover": {
                    bgcolor: pipeline === "whatsapp" ? "#4f46e5" : "#F9FAFB",
                  },
                }}
              >
                WhatsApp Board
              </Button>
            </ButtonGroup> */}

            <Box sx={{ display: { xs: "none", md: "flex" }, alignItems: "center", ml: 1, px: 2, borderLeft: "1px solid #E5E7EB" }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: "#64748B", letterSpacing: "0.05em" }}>
                TOTAL LEADS:
              </Typography>
              <Typography variant="body2" sx={{ ml: 1, fontWeight: 800, color: "#6366F1" }}>
                {totalDeals}
              </Typography>
              {exporting && (
                <Typography variant="caption" sx={{ ml: 1.5, color: "#64748B" }}>
                  Exporting…
                </Typography>
              )}
            </Box>

            {/* Mobile View Toggler */}
            <IconButton
              sx={{ display: { xs: "block", sm: "none" }, color: "#4B5563" }}
              onClick={handleMenuOpen}
            >
              <CircleChevronDown size={24} />
            </IconButton>

            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              TransitionComponent={Fade}
              PaperProps={{ sx: { borderRadius: 2, mt: 1, boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)' } }}
            >
              <MenuItem onClick={() => { onViewChange(VIEW_TYPES.BOARD); handleMenuClose(); }}>Board View</MenuItem>
              <MenuItem onClick={() => { onViewChange(VIEW_TYPES.LIST); handleMenuClose(); }}>List View</MenuItem>
              <MenuItem onClick={() => { onViewChange(VIEW_TYPES.APPOINTMENTS); handleMenuClose(); }}>Follow Ups</MenuItem>
            </Menu>
          </Box>

          {/* Right Side - Grouped Actions & CTA */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box sx={{ display: { xs: "none", sm: "block" } }}>
              <Button
                variant="outlined"
                onClick={handleActionMenuOpen}
                startIcon={<Settings2 size={18} />}
                endIcon={<ChevronDown size={14} style={{ transform: actionAnchorEl ? 'rotate(180deg)' : 'none', transition: '0.2s' }} />}
                sx={{
                  borderRadius: "12px", borderColor: "#E5E7EB", color: "#4B5563", textTransform: "none", fontWeight: 600, px: 2, py: 1,
                  "&:hover": { borderColor: "#6366f1", color: "#6366f1", bgcolor: "#F5F7FF" },
                }}
              >
                Tools
              </Button>
              <Menu
                anchorEl={actionAnchorEl}
                open={Boolean(actionAnchorEl)}
                onClose={handleActionMenuClose}
                TransitionComponent={Fade}
                PaperProps={{
                  sx: { borderRadius: 3, mt: 1.5, minWidth: 200, boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)', border: '1px solid #F3F4F6', p: 1 }
                }}
              >
                <MenuItem onClick={handleShareLink} sx={{ borderRadius: 1.5, py: 1.2 }}>
                  <ListItemIcon><Share2 size={18} color="#6B7280" /></ListItemIcon>
                  <ListItemText primary="Share Lead Form" primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }} />
                </MenuItem>
                <MenuItem onClick={() => { setExportModalOpen(true); handleActionMenuClose(); }} sx={{ borderRadius: 1.5, py: 1.2 }}>
                  <ListItemIcon><FileSpreadsheet size={18} color="#6366F1" /></ListItemIcon>
                  <ListItemText primary="Export Leads (Custom)" primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }} />
                </MenuItem>
                <MenuItem onClick={() => { setUploadSheetModalOpen(true); handleActionMenuClose(); }} sx={{ borderRadius: 1.5, py: 1.2 }}>
                  <ListItemIcon><Upload size={18} color="#6366F1" /></ListItemIcon>
                  <ListItemText primary="Bulk Import Leads" primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }} />
                </MenuItem>
                <MenuItem onClick={() => { setGoogleSheetsModalOpen(true); handleActionMenuClose(); }} sx={{ borderRadius: 1.5, py: 1.2 }}>
                  <ListItemIcon><FileSpreadsheet size={18} color="#0F9D58" /></ListItemIcon>
                  <ListItemText primary="Google Sheets" primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }} />
                </MenuItem>
                {(userRole !== "viewer" && userRole !== "account manager") && (
                  <MenuItem onClick={() => { setAutoAssignModalOpen(true); handleActionMenuClose(); }} sx={{ borderRadius: 1.5, py: 1.2 }}>
                    <ListItemIcon><UserPlus size={18} color="#9333ea" /></ListItemIcon>
                    <ListItemText primary="Auto-Assign Settings" primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }} />
                  </MenuItem>
                )}
                {(userRole !== "viewer" && userRole !== "account manager") && (
                  <MenuItem onClick={() => { setNotificationModalOpen(true); handleActionMenuClose(); }} sx={{ borderRadius: 1.5, py: 1.2 }}>
                    <ListItemIcon><Mail size={18} color="#4F46E5" /></ListItemIcon>
                    <ListItemText primary="Email Notifications" primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }} />
                  </MenuItem>
                )}
              </Menu>
            </Box>

            <Box>
              <Button
                variant="contained" onClick={onAddDeal}
                sx={{
                  borderRadius: "12px", bgcolor: "#6366f1", "&:hover": { bgcolor: "#4f46e5", transform: 'translateY(-1px)' },
                  textTransform: "none", fontWeight: 700, px: 3, py: 1, transition: 'all 0.2s', boxShadow: '0 4px 6px -1px rgba(99, 102, 241, 0.2)'
                }}
              >
                New Lead
              </Button>
            </Box>

            <Box sx={{ display: { xs: "flex", sm: "none" }, gap: 1 }}>
              <IconButton onClick={handleShareLink} sx={{ border: "1px solid #E5E7EB" }}>
                <Share2 size={18} color="#6B7280" />
              </IconButton>
              <IconButton onClick={handleActionMenuOpen} sx={{ border: "1px solid #E5E7EB" }}>
                <Settings2 size={18} color="#6366F1" />
              </IconButton>
            </Box>
          </Box>
        </Toolbar>
      </AppBar>

      <Snackbar
        open={snackbarOpen} autoHideDuration={3000} onClose={() => setSnackbarOpen(false)}
        message="Lead form link copied to clipboard!" anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />

      <GoogleSheetsModal open={googleSheetsModalOpen} onClose={() => setGoogleSheetsModalOpen(false)} onSyncComplete={onRefreshDeals} />
      <UploadSheetModal open={uploadSheetModalOpen} onClose={() => setUploadSheetModalOpen(false)} onUploadComplete={onRefreshDeals} pipeline={pipeline} />
      <AutoAssignSettings open={autoAssignModalOpen} onClose={() => setAutoAssignModalOpen(false)} userId={userId} />
      <LeadNotificationSettings open={notificationModalOpen} onClose={() => setNotificationModalOpen(false)} userId={userId} />
      <ExportSelectionModal open={exportModalOpen} onClose={() => setExportModalOpen(false)} onExport={handleExportLeads} />
    </>
  );
};
